import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Save, Trash2, Video, CircleDot, CircleOff, Lock, LogOut, Link2, Upload,
  ScanLine, PlayCircle, Loader2, Image as ImageIcon, CheckCircle2, MessageCircle,
  QrCode, Instagram, Facebook, IdCard, Youtube, Image as ImageIcon2, Music, FileText, Film,
  CalendarDays, Users, Clock, HandHeart,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  publishVideo, unpublishVideo, uploadVideoChunked, uploadCover, getStats, getDailyStats, getHourlyStats, getAmenStats,
  getContact, saveContact, clearContact,
} from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { VideoPreview } from "./VideoPreview";
import { ChangePasswordDialog } from "./ChangePasswordDialog";
import { TikTokIcon } from "./TikTokIcon";

const MediaPreview = ({ mediaType, file, url, current }) => {
  const [blobUrl, setBlobUrl] = useState(null);
  useEffect(() => {
    if (file) {
      const u = URL.createObjectURL(file);
      setBlobUrl(u);
      return () => URL.revokeObjectURL(u);
    }
    setBlobUrl(null);
  }, [file]);

  const src = blobUrl || url || (current?.published ? current.url : "");
  const label = { image: "Prévia da imagem", audio: "Prévia do áudio", link: "Link" }[mediaType];

  return (
    <div>
      <p className="mb-2 flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest text-muted-foreground">
        {mediaType === "image" ? <ImageIcon2 className="h-3.5 w-3.5" /> : mediaType === "audio" ? <Music className="h-3.5 w-3.5" /> : <FileText className="h-3.5 w-3.5" />} {label}
      </p>
      <div data-testid="content-preview" className="overflow-hidden rounded-xl border border-border bg-secondary/40 p-4">
        {mediaType === "image" && (src
          ? <img src={src} alt="Prévia" data-testid="content-preview-image" className="mx-auto max-h-64 rounded-lg object-contain" />
          : <p className="py-8 text-center text-sm text-muted-foreground">Selecione ou cole uma imagem</p>)}
        {mediaType === "audio" && (src
          ? <audio src={src} controls data-testid="content-preview-audio" className="w-full" />
          : <p className="py-8 text-center text-sm text-muted-foreground">Selecione ou cole um áudio</p>)}
        {mediaType === "link" && (src
          ? <p className="truncate text-center font-mono text-sm text-foreground" data-testid="content-preview-link">{src}</p>
          : <p className="py-8 text-center text-sm text-muted-foreground">Cole um link para abrir</p>)}
      </div>
    </div>
  );
};

const LoginGate = () => {
  const { signIn } = useAuth();
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!password) return;
    try {
      setLoading(true);
      await signIn(password);
      toast.success("Bem-vindo(a)! Você está no painel.");
    } catch {
      toast.error("Senha incorreta.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center px-6 py-14 text-center">
      <span className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary">
        <Lock className="h-7 w-7" />
      </span>
      <h3 className="font-display text-xl font-semibold">Área do administrador</h3>
      <p className="mb-6 mt-1 text-sm text-muted-foreground">
        Digite a senha para publicar ou trocar o vídeo.
      </p>
      <form onSubmit={handleLogin} className="w-full max-w-xs space-y-3">
        <Input
          type="password"
          data-testid="admin-login-password-input"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Senha de admin"
          className="h-11 rounded-xl text-center"
        />
        <Button type="submit" data-testid="admin-login-button" disabled={loading} size="lg" className="h-11 w-full rounded-xl">
          {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Lock className="mr-2 h-4 w-4" />}
          Entrar
        </Button>
      </form>
    </div>
  );
};

const ToggleBlock = ({ icon: Icon, color, title, subtitle, enabled, onToggle, testid, children }) => (
  <div className="rounded-2xl border border-border bg-secondary/30 p-4">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <span className="flex h-9 w-9 items-center justify-center rounded-lg" style={{ backgroundColor: `${color}22`, color }}>
          <Icon className="h-4 w-4" />
        </span>
        <div>
          <Label htmlFor={testid} className="text-sm font-semibold">{title}</Label>
          <p className="text-xs text-muted-foreground">{subtitle}</p>
        </div>
      </div>
      <Switch id={testid} data-testid={testid} checked={enabled} onCheckedChange={onToggle} />
    </div>
    {enabled && <div className="mt-4 space-y-3">{children}</div>}
  </div>
);

const dayLabel = (dateStr, index) => {
  if (index === 0) return "Hoje";
  if (index === 1) return "Ontem";
  const [y, m, d] = dateStr.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  const wd = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"][dt.getDay()];
  return `${wd} ${String(d).padStart(2, "0")}/${String(m).padStart(2, "0")}`;
};

const fmtHour = (h) => `${String(h).padStart(2, "0")}h`;

export const AdminManagerPanel = ({ video, onChanged, qrId = "main" }) => {
  const { authed, signOut } = useAuth();

  // Video state
  const [mediaType, setMediaType] = useState("video"); // video | image | audio | link
  const [mode, setMode] = useState("upload");
  const [sourceType, setSourceType] = useState("youtube");
  const [url, setUrl] = useState("");
  const [title, setTitle] = useState("");
  const [coverUrl, setCoverUrl] = useState("");
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState(0);
  const [phase, setPhase] = useState("idle");
  const [coverUploading, setCoverUploading] = useState(false);
  const [confirmReplace, setConfirmReplace] = useState(false);
  const [confirmRemoveVideo, setConfirmRemoveVideo] = useState(false);

  // Contact state
  const [waEnabled, setWaEnabled] = useState(false);
  const [waNumber, setWaNumber] = useState("");
  const [waMessage, setWaMessage] = useState("");
  const [pixEnabled, setPixEnabled] = useState(false);
  const [pixKey, setPixKey] = useState("");
  const [pixMessage, setPixMessage] = useState("");
  const [igEnabled, setIgEnabled] = useState(false);
  const [igUrl, setIgUrl] = useState("");
  const [fbEnabled, setFbEnabled] = useState(false);
  const [fbUrl, setFbUrl] = useState("");
  const [ytEnabled, setYtEnabled] = useState(false);
  const [ytUrl, setYtUrl] = useState("");
  const [ttEnabled, setTtEnabled] = useState(false);
  const [ttUrl, setTtUrl] = useState("");
  const [amenEnabled, setAmenEnabled] = useState(true);
  const [amenPrompt, setAmenPrompt] = useState("Orou conosco hoje?");
  const [amenSubtitle, setAmenSubtitle] = useState("Aperte no botão Amém.");
  const [amenButton, setAmenButton] = useState("Amém");
  const [amenThanks, setAmenThanks] = useState("Amém! 🙏");
  const [amenThanksSub, setAmenThanksSub] = useState("Sua oração foi registrada. Que Deus abençoe você.");
  const [savingContact, setSavingContact] = useState(false);
  const [confirmClearContact, setConfirmClearContact] = useState(false);

  const [stats, setStats] = useState({ scans: 0, plays: 0 });
  const [daily, setDaily] = useState({ today: 0, days: [] });
  const [hourly, setHourly] = useState({ hours: [], total: 0, peak_hour: null, peak_count: 0 });
  const [amen, setAmen] = useState({ today: 0, total: 0, days: [], today_hourly: [], recent_today: [] });

  const published = video?.published;

  // Prefill video fields from the current published video.
  useEffect(() => {
    if (video?.published) {
      setTitle(video.title || "");
      setCoverUrl(video.cover_url || "");
      setMediaType(video.media_type || "video");
      if (video.source_type === "mp4" || video.source_type === "upload") {
        setMode("upload");
      } else {
        setMode("link");
        setSourceType(video.source_type || "youtube");
        setUrl(video.url || "");
      }
    }
  }, [video]);

  const loadContact = () =>
    getContact(qrId)
      .then((c) => {
        setWaEnabled(!!c.whatsapp_enabled);
        setWaNumber(c.whatsapp_number || "");
        setWaMessage(c.whatsapp_message || "");
        setPixEnabled(!!c.pix_enabled);
        setPixKey(c.pix_key || "");
        setPixMessage(c.pix_message || "");
        setIgEnabled(!!c.instagram_enabled);
        setIgUrl(c.instagram_url || "");
        setFbEnabled(!!c.facebook_enabled);
        setFbUrl(c.facebook_url || "");
        setYtEnabled(!!c.youtube_enabled);
        setYtUrl(c.youtube_url || "");
        setTtEnabled(!!c.tiktok_enabled);
        setTtUrl(c.tiktok_url || "");
        setAmenEnabled(c.amen_enabled !== false);
        setAmenPrompt(c.amen_prompt || "Orou conosco hoje?");
        setAmenSubtitle(c.amen_subtitle || "Aperte no botão Amém.");
        setAmenButton(c.amen_button || "Amém");
        setAmenThanks(c.amen_thanks || "Amém! 🙏");
        setAmenThanksSub(c.amen_thanks_sub || "Sua oração foi registrada. Que Deus abençoe você.");
      })
      .catch(() => {});

  useEffect(() => {
    if (authed) {
      getStats(qrId).then(setStats).catch(() => {});
      loadContact();
    }
  }, [authed]);

  // Live daily access counter (refreshes every 5s while the panel is open).
  useEffect(() => {
    if (!authed) return;
    const refresh = () => {
      getStats(qrId).then(setStats).catch(() => {});
      getDailyStats(qrId).then(setDaily).catch(() => {});
      getHourlyStats(qrId).then(setHourly).catch(() => {});
      getAmenStats(qrId).then(setAmen).catch(() => {});
    };
    refresh();
    const t = setInterval(refresh, 5000);
    return () => clearInterval(t);
  }, [authed, qrId]);

  // ---- Content actions ----
  const isUploadKind = mediaType === "video" || mediaType === "image" || mediaType === "audio";

  const handleSaveLink = async () => {
    try {
      setSaving(true);
      const st = mediaType === "video" ? sourceType : "url";
      await publishVideo({ source_type: st, url, title, description: "", cover_url: coverUrl, media_type: mediaType }, qrId);
      toast.success("Conteúdo publicado! A página pública já está atualizada.");
      onChanged?.();
    } catch {
      toast.error("Não foi possível publicar o conteúdo.");
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async () => {
    try {
      setSaving(true);
      setProgress(0);
      setPhase("uploading");
      await uploadVideoChunked(
        file,
        { title, cover_url: coverUrl, media_type: mediaType },
        (p) => {
          setProgress(p);
          if (p >= 100) setPhase("processing");
        },
        qrId,
      );
      setPhase("done");
      toast.success("Conteúdo enviado e publicado com sucesso!");
      setFile(null);
      onChanged?.();
      setTimeout(() => setPhase("idle"), 2500);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Falha ao enviar o arquivo.";
      toast.error(typeof msg === "string" ? msg : "Falha ao enviar o arquivo.");
      setPhase("idle");
    } finally {
      setSaving(false);
      setProgress(0);
    }
  };

  // Link kind is always URL-based; others use the upload/link mode toggle.
  const usesUpload = isUploadKind && mode === "upload";
  const doPublish = () => (usesUpload ? handleUpload() : handleSaveLink());

  const handlePublishClick = () => {
    if (usesUpload && !file) return toast.error("Selecione um arquivo.");
    if (!usesUpload && !url.trim()) return toast.error("Informe o link/URL.");
    if (published) setConfirmReplace(true);
    else doPublish();
  };

  const handleRemoveVideo = async () => {
    try {
      await unpublishVideo(qrId);
      setUrl("");
      setTitle("");
      setCoverUrl("");
      setFile(null);
      toast.success("Vídeo removido. Suas informações de contato foram mantidas.");
      onChanged?.();
    } catch {
      toast.error("Não foi possível remover o vídeo.");
    }
  };

  const handleCoverUpload = async (e) => {
    const img = e.target.files?.[0];
    if (!img) return;
    const fd = new FormData();
    fd.append("file", img);
    try {
      setCoverUploading(true);
      const { url: coverImgUrl } = await uploadCover(fd, qrId);
      setCoverUrl(coverImgUrl);
      toast.success("Capa enviada! Lembre-se de publicar para aplicar.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Falha ao enviar a capa.";
      toast.error(typeof msg === "string" ? msg : "Falha ao enviar a capa.");
    } finally {
      setCoverUploading(false);
      e.target.value = "";
    }
  };

  // ---- Contact actions ----
  const handleSaveContact = async () => {
    try {
      setSavingContact(true);
      await saveContact({
        whatsapp_enabled: waEnabled, whatsapp_number: waNumber, whatsapp_message: waMessage,
        pix_enabled: pixEnabled, pix_key: pixKey, pix_message: pixMessage,
        instagram_enabled: igEnabled, instagram_url: igUrl,
        facebook_enabled: fbEnabled, facebook_url: fbUrl,
        youtube_enabled: ytEnabled, youtube_url: ytUrl,
        tiktok_enabled: ttEnabled, tiktok_url: ttUrl,
        amen_enabled: amenEnabled, amen_prompt: amenPrompt, amen_subtitle: amenSubtitle,
        amen_button: amenButton, amen_thanks: amenThanks, amen_thanks_sub: amenThanksSub,
      }, qrId);
      toast.success("Informações salvas! Elas aparecem na página do vídeo.");
    } catch {
      toast.error("Não foi possível salvar as informações.");
    } finally {
      setSavingContact(false);
    }
  };

  const handleClearContact = async () => {
    try {
      await clearContact(qrId);
      setWaEnabled(false); setWaNumber(""); setWaMessage("");
      setPixEnabled(false); setPixKey(""); setPixMessage("");
      setIgEnabled(false); setIgUrl("");
      setFbEnabled(false); setFbUrl("");
      setYtEnabled(false); setYtUrl("");
      setTtEnabled(false); setTtUrl("");
      toast.success("Informações removidas. O vídeo continua no ar.");
    } catch {
      toast.error("Não foi possível remover as informações.");
    }
  };

  return (
    <motion.div
      data-testid="admin-panel-tab"
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: "easeOut", delay: 0.1 }}
      className="rounded-3xl border border-border bg-card p-6 shadow-sm sm:p-8"
    >
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-secondary text-foreground">
            <Video className="h-5 w-5" />
          </span>
          <div>
            <h3 className="font-display text-xl font-semibold">Painel</h3>
            <p className="text-sm text-muted-foreground">Vídeo e informações de contato</p>
          </div>
        </div>
        {authed && (
          <div className="flex items-center gap-2">
            <span
              data-testid="admin-scan-count-badge"
              className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium ${
                published
                  ? "border-emerald-500/20 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
                  : "border-amber-500/20 bg-amber-500/10 text-amber-600 dark:text-amber-400"
              }`}
            >
              {published ? <CircleDot className="h-3.5 w-3.5" /> : <CircleOff className="h-3.5 w-3.5" />}
              {published ? "No ar" : "Sem vídeo"}
            </span>
            <ChangePasswordDialog />
            <Button
              variant="ghost" size="icon" data-testid="admin-logout-button" onClick={signOut}
              className="h-8 w-8 rounded-full text-muted-foreground" aria-label="Sair"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>

      {!authed ? (
        <LoginGate />
      ) : (
        <div className="space-y-8">
          {/* ---------------- CONTENT SECTION ---------------- */}
          <section className="space-y-5">
            <div className="flex items-center gap-2">
              <Film className="h-4 w-4 text-primary" />
              <h4 className="font-display text-sm font-bold uppercase tracking-wide text-muted-foreground">Conteúdo</h4>
            </div>

            <div>
              <Label className="mb-2 block text-sm">Tipo de conteúdo</Label>
              <Select value={mediaType} onValueChange={(v) => { setMediaType(v); setFile(null); if (v !== "video") setMode("upload"); }}>
                <SelectTrigger data-testid="admin-media-type-select" className="h-11 rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="video">Vídeo</SelectItem>
                  <SelectItem value="image">Imagem</SelectItem>
                  <SelectItem value="audio">Áudio</SelectItem>
                  <SelectItem value="link">Link</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {mediaType === "video" ? (
              <VideoPreview mode={mode} file={file} sourceType={sourceType} url={url} current={video} />
            ) : (
              <MediaPreview mediaType={mediaType} file={mode === "upload" ? file : null} url={mode === "link" || mediaType === "link" ? url : ""} current={video?.media_type === mediaType ? video : null} />
            )}

            {mediaType === "link" ? (
              <div>
                <Label htmlFor="content-link" className="mb-2 block text-sm">Link para abrir</Label>
                <Input
                  id="content-link" data-testid="admin-video-url-input" value={url}
                  onChange={(e) => setUrl(e.target.value)} placeholder="https://…"
                  className="h-11 rounded-xl font-mono text-sm"
                />
              </div>
            ) : (
              <>
                <div className="inline-flex w-full rounded-xl border border-border bg-secondary/50 p-1">
                  <button
                    data-testid="admin-mode-upload" onClick={() => setMode("upload")}
                    className={`flex flex-1 items-center justify-center gap-2 rounded-lg py-2 text-sm font-medium transition-colors ${mode === "upload" ? "bg-background text-foreground shadow-sm" : "text-muted-foreground"}`}
                  >
                    <Upload className="h-4 w-4" /> Enviar arquivo
                  </button>
                  <button
                    data-testid="admin-mode-link" onClick={() => setMode("link")}
                    className={`flex flex-1 items-center justify-center gap-2 rounded-lg py-2 text-sm font-medium transition-colors ${mode === "link" ? "bg-background text-foreground shadow-sm" : "text-muted-foreground"}`}
                  >
                    <Link2 className="h-4 w-4" /> Colar link
                  </button>
                </div>

                {mode === "upload" ? (
                  <div>
                    <Label htmlFor="content-file" className="mb-2 block text-sm">
                      {mediaType === "video" && "Arquivo de vídeo (MP4, MOV e outros — até 5 GB)"}
                      {mediaType === "image" && "Arquivo de imagem (JPG, PNG, WEBP…)"}
                      {mediaType === "audio" && "Arquivo de áudio (MP3, WAV, M4A…)"}
                    </Label>
                    <Input
                      id="content-file" type="file"
                      accept={mediaType === "video" ? "video/*" : mediaType === "image" ? "image/*" : "audio/*"}
                      data-testid="admin-video-file-input"
                      onChange={(e) => setFile(e.target.files?.[0] || null)}
                      className="h-11 cursor-pointer rounded-xl file:mr-3 file:rounded-md file:border-0 file:bg-secondary file:px-3 file:py-1 file:text-sm"
                    />
                    {file && (
                      <p className="mt-2 truncate text-xs text-muted-foreground">
                        {file.name} — {(file.size / (1024 * 1024)).toFixed(1)} MB
                      </p>
                    )}
                    {(phase === "uploading" || phase === "processing" || phase === "done") && (
                      <div data-testid="upload-progress-block" className="mt-4 rounded-xl border border-border bg-secondary/40 p-4">
                        <div className="mb-2 flex items-center justify-between text-sm font-medium">
                          <span className="flex items-center gap-2">
                            {phase === "done" ? <CheckCircle2 className="h-4 w-4 text-emerald-500" /> : <Loader2 className="h-4 w-4 animate-spin text-primary" />}
                            {phase === "uploading" && "Enviando arquivo…"}
                            {phase === "processing" && "Processando e publicando…"}
                            {phase === "done" && "Concluído!"}
                          </span>
                          {phase === "uploading" && <span data-testid="upload-progress-percent" className="font-mono text-primary">{progress}%</span>}
                        </div>
                        <div className="h-2.5 w-full overflow-hidden rounded-full bg-secondary">
                          <div
                            className={`h-full rounded-full transition-all duration-300 ${phase === "done" ? "bg-emerald-500" : "bg-primary"} ${phase === "processing" ? "animate-pulse" : ""}`}
                            style={{ width: phase === "uploading" ? `${progress}%` : "100%" }}
                          />
                        </div>
                        {phase === "processing" && <p className="mt-2 text-xs text-muted-foreground">Arquivos grandes podem levar alguns instantes. Não feche esta janela.</p>}
                      </div>
                    )}
                  </div>
                ) : (
                  <>
                    {mediaType === "video" && (
                      <div>
                        <Label className="mb-2 block text-sm">Tipo de fonte</Label>
                        <Select value={sourceType} onValueChange={setSourceType}>
                          <SelectTrigger data-testid="admin-source-type-select" className="h-11 rounded-xl"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="youtube">YouTube</SelectItem>
                            <SelectItem value="vimeo">Vimeo</SelectItem>
                            <SelectItem value="url">Outro link</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    <div>
                      <Label htmlFor="content-url" className="mb-2 block text-sm">
                        {mediaType === "video" ? "URL do vídeo" : mediaType === "image" ? "URL da imagem" : "URL do áudio"}
                      </Label>
                      <Input
                        id="content-url" data-testid="admin-video-url-input" value={url}
                        onChange={(e) => setUrl(e.target.value)} placeholder="https://…"
                        className="h-11 rounded-xl font-mono text-sm"
                      />
                    </div>
                  </>
                )}
              </>
            )}

            <div>
              <Label htmlFor="video-title" className="mb-2 block text-sm">Título (opcional)</Label>
              <Input
                id="video-title" data-testid="admin-video-title-input" value={title}
                onChange={(e) => setTitle(e.target.value)} placeholder="Ex.: Conheça nossa história"
                className="h-11 rounded-xl"
              />
            </div>

            {mediaType === "video" && (
              <div>
                <Label htmlFor="video-cover" className="mb-2 block text-sm">Imagem de capa (opcional)</Label>
                <Input
                  id="video-cover" data-testid="admin-video-cover-input" value={coverUrl}
                  onChange={(e) => setCoverUrl(e.target.value)}
                  placeholder="Cole uma URL ou envie do aparelho — vazio = capa automática"
                  className="h-11 rounded-xl font-mono text-sm"
                />
                <div className="mt-2 flex items-center gap-3">
                  <label htmlFor="cover-file" data-testid="admin-cover-upload-label"
                    className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-secondary px-3 py-2 text-sm font-medium transition-colors hover:bg-secondary/70">
                    {coverUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImageIcon className="h-4 w-4" />}
                    {coverUploading ? "Enviando capa…" : "Enviar imagem do aparelho"}
                  </label>
                  <input id="cover-file" type="file" accept="image/*" data-testid="admin-cover-file-input"
                    onChange={handleCoverUpload} disabled={coverUploading} className="hidden" />
                  {coverUrl && <img src={coverUrl} alt="Prévia da capa" data-testid="admin-cover-thumb" className="h-10 w-16 rounded-md border border-border object-cover" />}
                </div>
              </div>
            )}

            <div className="flex flex-col gap-3 sm:flex-row">
              <Button data-testid="admin-save-video-button" onClick={handlePublishClick} disabled={saving} size="lg"
                className="h-12 flex-1 rounded-xl transition-transform active:scale-[0.98]">
                {saving ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : <Save className="mr-2 h-5 w-5" />}
                {saving ? (usesUpload ? `Enviando… ${progress}%` : "Publicando…") : "Publicar conteúdo"}
              </Button>
              <Button data-testid="admin-remove-video-button" onClick={() => setConfirmRemoveVideo(true)} disabled={!published} variant="outline" size="lg"
                className="h-12 rounded-xl text-destructive hover:text-destructive">
                <Trash2 className="mr-2 h-5 w-5" /> Remover conteúdo
              </Button>
            </div>
          </section>

          <div className="h-px w-full bg-border" />

          {/* ---------------- CONTACT SECTION ---------------- */}
          <section className="space-y-4">
            <div className="flex items-center gap-2">
              <IdCard className="h-4 w-4 text-primary" />
              <h4 className="font-display text-sm font-bold uppercase tracking-wide text-muted-foreground">Informações de contato</h4>
            </div>
            <p className="-mt-1 text-xs text-muted-foreground">
              Salvas separadamente do vídeo — trocar o vídeo não apaga estas informações.
            </p>

            <ToggleBlock icon={MessageCircle} color="#25D366" title="Botão de WhatsApp" subtitle="Exibir na página do vídeo"
              enabled={waEnabled} onToggle={setWaEnabled} testid="admin-whatsapp-toggle">
              <div>
                <Label htmlFor="wa-number" className="mb-2 block text-sm">Número (com DDI e DDD)</Label>
                <Input id="wa-number" data-testid="admin-whatsapp-number-input" value={waNumber} onChange={(e) => setWaNumber(e.target.value)} placeholder="Ex.: +55 11 99999-9999" className="h-11 rounded-xl" />
              </div>
              <div>
                <Label htmlFor="wa-message" className="mb-2 block text-sm">Recado exibido ao visitante (opcional)</Label>
                <Textarea id="wa-message" data-testid="admin-whatsapp-message-input" value={waMessage} onChange={(e) => setWaMessage(e.target.value)} placeholder="Ex.: Envie seu pedido pelo WhatsApp" rows={2} className="rounded-xl" />
              </div>
            </ToggleBlock>

            <ToggleBlock icon={QrCode} color="#0d9488" title="Chave Pix" subtitle="Exibir na página do vídeo"
              enabled={pixEnabled} onToggle={setPixEnabled} testid="admin-pix-toggle">
              <div>
                <Label htmlFor="pix-key" className="mb-2 block text-sm">Chave Pix</Label>
                <Input id="pix-key" data-testid="admin-pix-key-input" value={pixKey} onChange={(e) => setPixKey(e.target.value)} placeholder="CPF, e-mail, telefone ou chave aleatória" className="h-11 rounded-xl" />
              </div>
              <div>
                <Label htmlFor="pix-message" className="mb-2 block text-sm">Recado exibido ao visitante (opcional)</Label>
                <Textarea id="pix-message" data-testid="admin-pix-message-input" value={pixMessage} onChange={(e) => setPixMessage(e.target.value)} placeholder="Ex.: Contribua com nossa obra via Pix" rows={2} className="rounded-xl" />
              </div>
            </ToggleBlock>

            <ToggleBlock icon={Instagram} color="#E1306C" title="Instagram" subtitle="Exibir na página do vídeo"
              enabled={igEnabled} onToggle={setIgEnabled} testid="admin-instagram-toggle">
              <div>
                <Label htmlFor="ig-url" className="mb-2 block text-sm">Link do Instagram</Label>
                <Input id="ig-url" data-testid="admin-instagram-url-input" value={igUrl} onChange={(e) => setIgUrl(e.target.value)} placeholder="https://instagram.com/seuperfil" className="h-11 rounded-xl font-mono text-sm" />
              </div>
            </ToggleBlock>

            <ToggleBlock icon={Facebook} color="#1877F2" title="Facebook" subtitle="Exibir na página do vídeo"
              enabled={fbEnabled} onToggle={setFbEnabled} testid="admin-facebook-toggle">
              <div>
                <Label htmlFor="fb-url" className="mb-2 block text-sm">Link do Facebook</Label>
                <Input id="fb-url" data-testid="admin-facebook-url-input" value={fbUrl} onChange={(e) => setFbUrl(e.target.value)} placeholder="https://facebook.com/suapagina" className="h-11 rounded-xl font-mono text-sm" />
              </div>
            </ToggleBlock>

            <ToggleBlock icon={Youtube} color="#FF0000" title="YouTube" subtitle="Exibir na página do vídeo"
              enabled={ytEnabled} onToggle={setYtEnabled} testid="admin-youtube-toggle">
              <div>
                <Label htmlFor="yt-url" className="mb-2 block text-sm">Link do canal do YouTube</Label>
                <Input id="yt-url" data-testid="admin-youtube-url-input" value={ytUrl} onChange={(e) => setYtUrl(e.target.value)} placeholder="https://youtube.com/@seucanal" className="h-11 rounded-xl font-mono text-sm" />
              </div>
            </ToggleBlock>

            <ToggleBlock icon={TikTokIcon} color="#111827" title="TikTok" subtitle="Exibir na página do vídeo"
              enabled={ttEnabled} onToggle={setTtEnabled} testid="admin-tiktok-toggle">
              <div>
                <Label htmlFor="tt-url" className="mb-2 block text-sm">Link do TikTok</Label>
                <Input id="tt-url" data-testid="admin-tiktok-url-input" value={ttUrl} onChange={(e) => setTtUrl(e.target.value)} placeholder="https://tiktok.com/@seuperfil" className="h-11 rounded-xl font-mono text-sm" />
              </div>
            </ToggleBlock>

            <ToggleBlock icon={HandHeart} color="#6d28d9" title="Botão Amém (oração)" subtitle="Exibir na página do vídeo"
              enabled={amenEnabled} onToggle={setAmenEnabled} testid="admin-amen-toggle">
              <div>
                <Label htmlFor="amen-prompt" className="mb-2 block text-sm">Frase (título do botão)</Label>
                <Input id="amen-prompt" data-testid="admin-amen-prompt-input" value={amenPrompt} onChange={(e) => setAmenPrompt(e.target.value)} placeholder="Ex.: Orou conosco hoje?" className="h-11 rounded-xl" />
              </div>
              <div>
                <Label htmlFor="amen-subtitle" className="mb-2 block text-sm">Texto abaixo da frase</Label>
                <Input id="amen-subtitle" data-testid="admin-amen-subtitle-input" value={amenSubtitle} onChange={(e) => setAmenSubtitle(e.target.value)} placeholder="Ex.: Aperte no botão Amém." className="h-11 rounded-xl" />
              </div>
              <div>
                <Label htmlFor="amen-button" className="mb-2 block text-sm">Texto do botão</Label>
                <Input id="amen-button" data-testid="admin-amen-button-input" value={amenButton} onChange={(e) => setAmenButton(e.target.value)} placeholder="Ex.: Amém" className="h-11 rounded-xl" />
              </div>
              <div>
                <Label htmlFor="amen-thanks" className="mb-2 block text-sm">Agradecimento (título, após apertar)</Label>
                <Input id="amen-thanks" data-testid="admin-amen-thanks-input" value={amenThanks} onChange={(e) => setAmenThanks(e.target.value)} placeholder="Ex.: Amém! 🙏" className="h-11 rounded-xl" />
              </div>
              <div>
                <Label htmlFor="amen-thanks-sub" className="mb-2 block text-sm">Agradecimento (texto, após apertar)</Label>
                <Textarea id="amen-thanks-sub" data-testid="admin-amen-thanks-sub-input" value={amenThanksSub} onChange={(e) => setAmenThanksSub(e.target.value)} placeholder="Ex.: Sua oração foi registrada. Que Deus abençoe você." rows={2} className="rounded-xl" />
              </div>
            </ToggleBlock>

            <div className="flex flex-col gap-3 sm:flex-row">
              <Button data-testid="admin-save-contact-button" onClick={handleSaveContact} disabled={savingContact} size="lg"
                className="h-12 flex-1 rounded-xl transition-transform active:scale-[0.98]">
                {savingContact ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : <Save className="mr-2 h-5 w-5" />}
                Salvar informações
              </Button>
              <Button data-testid="admin-remove-contact-button" onClick={() => setConfirmClearContact(true)} variant="outline" size="lg"
                className="h-12 rounded-xl text-destructive hover:text-destructive">
                <Trash2 className="mr-2 h-5 w-5" /> Remover informações
              </Button>
            </div>
          </section>

          <div className="flex items-center justify-center gap-6 border-t border-border pt-4 text-xs text-muted-foreground">
            <span className="flex items-center gap-1.5"><ScanLine className="h-3.5 w-3.5" /><b data-testid="stat-scans-value" className="text-foreground">{stats.scans}</b> escaneamentos</span>
            <span className="flex items-center gap-1.5"><PlayCircle className="h-3.5 w-3.5" /><b data-testid="stat-plays-value" className="text-foreground">{stats.plays}</b> plays</span>
          </div>

          {/* ---------------- DAILY ACCESS COUNTER ---------------- */}
          <section data-testid="daily-access-section" className="space-y-4">
            <div className="flex items-center gap-2">
              <CalendarDays className="h-4 w-4 text-primary" />
              <h4 className="font-display text-sm font-bold uppercase tracking-wide text-muted-foreground">Acessos por dia</h4>
              <span className="ml-auto flex items-center gap-1.5 text-xs text-muted-foreground">
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-500 opacity-75" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
                </span>
                Ao vivo
              </span>
            </div>

            <div className="rounded-2xl border border-primary/20 bg-gradient-to-br from-primary/10 to-primary/5 p-5">
              <p className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                <Users className="h-3.5 w-3.5" /> Acessos de hoje
              </p>
              <p data-testid="daily-today-count" className="mt-1 font-display text-4xl font-bold tabular-nums text-foreground">
                {daily.today}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">Pessoas que abriram esta página hoje (atualiza sozinho)</p>
            </div>

            <div className="rounded-2xl border border-border bg-secondary/30 p-4">
              <p className="mb-3 text-xs font-medium uppercase tracking-wide text-muted-foreground">Últimos 7 dias</p>
              <ul data-testid="daily-last-7-list" className="space-y-1.5">
                {daily.days.map((d, i) => {
                  const max = Math.max(1, ...daily.days.map((x) => x.count));
                  const pct = Math.round((d.count / max) * 100);
                  return (
                    <li key={d.date} data-testid={`daily-day-row-${i}`} className="flex items-center gap-3">
                      <span className={`w-20 shrink-0 text-xs ${i === 0 ? "font-semibold text-foreground" : "text-muted-foreground"}`}>{dayLabel(d.date, i)}</span>
                      <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-secondary">
                        <div className="h-full rounded-full bg-primary transition-all duration-500" style={{ width: `${d.count > 0 ? Math.max(pct, 6) : 0}%` }} />
                      </div>
                      <b data-testid={`daily-day-count-${i}`} className="w-8 shrink-0 text-right text-sm tabular-nums text-foreground">{d.count}</b>
                    </li>
                  );
                })}
              </ul>
            </div>
          </section>

          {/* ---------------- PEAK HOUR ---------------- */}
          <section data-testid="peak-hour-section" className="space-y-4">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-primary" />
              <h4 className="font-display text-sm font-bold uppercase tracking-wide text-muted-foreground">Horário de pico</h4>
            </div>

            {hourly.total > 0 ? (
              <>
                <div className="rounded-2xl border border-primary/20 bg-gradient-to-br from-primary/10 to-primary/5 p-5">
                  <p className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    <Clock className="h-3.5 w-3.5" /> Horário mais movimentado
                  </p>
                  <p data-testid="peak-hour-label" className="mt-1 font-display text-3xl font-bold tabular-nums text-foreground">
                    {fmtHour(hourly.peak_hour)}<span className="text-muted-foreground"> – </span>{fmtHour((hourly.peak_hour + 1) % 24)}
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    <b className="text-foreground">{hourly.peak_count}</b> acessos neste horário (todos os dias somados)
                  </p>
                </div>

                <div className="rounded-2xl border border-border bg-secondary/30 p-4">
                  <p className="mb-3 text-xs font-medium uppercase tracking-wide text-muted-foreground">Acessos por hora do dia</p>
                  <div data-testid="hourly-bars" className="flex items-end gap-[3px]" style={{ height: 96 }}>
                    {hourly.hours.map((h) => {
                      const max = Math.max(1, ...hourly.hours.map((x) => x.count));
                      const pct = (h.count / max) * 100;
                      const isPeak = h.hour === hourly.peak_hour && h.count > 0;
                      return (
                        <div key={h.hour} className="group relative flex flex-1 flex-col items-center justify-end" style={{ height: "100%" }}>
                          <div
                            data-testid={`hourly-bar-${h.hour}`}
                            className={`w-full rounded-t transition-all duration-500 ${isPeak ? "bg-primary" : "bg-primary/35"}`}
                            style={{ height: `${h.count > 0 ? Math.max(pct, 4) : 0}%` }}
                          />
                          <span className="pointer-events-none absolute -top-6 hidden whitespace-nowrap rounded bg-foreground px-1.5 py-0.5 text-[10px] font-medium text-background group-hover:block">
                            {fmtHour(h.hour)} · {h.count}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                  <div className="mt-2 flex justify-between text-[10px] text-muted-foreground">
                    <span>00h</span><span>06h</span><span>12h</span><span>18h</span><span>23h</span>
                  </div>
                </div>
              </>
            ) : (
              <div data-testid="peak-hour-empty" className="rounded-2xl border border-dashed border-border bg-secondary/20 p-6 text-center text-sm text-muted-foreground">
                Ainda não há acessos suficientes para mostrar o horário de pico. Assim que as pessoas escanearem, ele aparece aqui.
              </div>
            )}
          </section>

          {/* ---------------- AMÉM (ORAÇÕES) ---------------- */}
          <section data-testid="amen-stats-section" className="space-y-4">
            <div className="flex items-center gap-2">
              <HandHeart className="h-4 w-4 text-primary" />
              <h4 className="font-display text-sm font-bold uppercase tracking-wide text-muted-foreground">Amém (orações)</h4>
              <span className="ml-auto flex items-center gap-1.5 text-xs text-muted-foreground">
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-500 opacity-75" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
                </span>
                Ao vivo
              </span>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-2xl border border-primary/20 bg-gradient-to-br from-primary/10 to-primary/5 p-5">
                <p className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  <HandHeart className="h-3.5 w-3.5" /> Oraram hoje
                </p>
                <p data-testid="amen-today-count" className="mt-1 font-display text-4xl font-bold tabular-nums text-foreground">{amen.today}</p>
              </div>
              <div className="rounded-2xl border border-border bg-secondary/30 p-5">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Total (sempre)</p>
                <p data-testid="amen-total-count" className="mt-1 font-display text-4xl font-bold tabular-nums text-foreground">{amen.total}</p>
              </div>
            </div>

            {/* Últimos 7 dias */}
            <div className="rounded-2xl border border-border bg-secondary/30 p-4">
              <p className="mb-3 text-xs font-medium uppercase tracking-wide text-muted-foreground">Orações — últimos 7 dias</p>
              <ul data-testid="amen-last-7-list" className="space-y-1.5">
                {amen.days.map((d, i) => {
                  const max = Math.max(1, ...amen.days.map((x) => x.count));
                  const pct = Math.round((d.count / max) * 100);
                  return (
                    <li key={d.date} className="flex items-center gap-3">
                      <span className={`w-20 shrink-0 text-xs ${i === 0 ? "font-semibold text-foreground" : "text-muted-foreground"}`}>{dayLabel(d.date, i)}</span>
                      <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-secondary">
                        <div className="h-full rounded-full bg-primary transition-all duration-500" style={{ width: `${d.count > 0 ? Math.max(pct, 6) : 0}%` }} />
                      </div>
                      <b className="w-8 shrink-0 text-right text-sm tabular-nums text-foreground">{d.count}</b>
                    </li>
                  );
                })}
              </ul>
            </div>

            {/* Horas em que oraram hoje */}
            <div className="rounded-2xl border border-border bg-secondary/30 p-4">
              <p className="mb-3 text-xs font-medium uppercase tracking-wide text-muted-foreground">Horas em que oraram hoje</p>
              {amen.today > 0 ? (
                <>
                  <div data-testid="amen-today-hourly" className="flex items-end gap-[3px]" style={{ height: 80 }}>
                    {amen.today_hourly.map((h) => {
                      const max = Math.max(1, ...amen.today_hourly.map((x) => x.count));
                      const pct = (h.count / max) * 100;
                      return (
                        <div key={h.hour} className="group relative flex flex-1 flex-col items-center justify-end" style={{ height: "100%" }}>
                          <div className={`w-full rounded-t transition-all duration-500 ${h.count > 0 ? "bg-primary" : "bg-primary/25"}`} style={{ height: `${h.count > 0 ? Math.max(pct, 6) : 0}%` }} />
                          <span className="pointer-events-none absolute -top-6 hidden whitespace-nowrap rounded bg-foreground px-1.5 py-0.5 text-[10px] font-medium text-background group-hover:block">
                            {String(h.hour).padStart(2, "0")}h · {h.count}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                  <div className="mt-2 flex justify-between text-[10px] text-muted-foreground">
                    <span>00h</span><span>06h</span><span>12h</span><span>18h</span><span>23h</span>
                  </div>
                  {amen.recent_today.length > 0 && (
                    <div className="mt-4">
                      <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">Horários de hoje</p>
                      <div data-testid="amen-recent-times" className="flex flex-wrap gap-1.5">
                        {amen.recent_today.map((t, i) => (
                          <span key={i} className="rounded-full border border-primary/20 bg-primary/10 px-2.5 py-1 text-xs font-medium tabular-nums text-primary">{t}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <p className="py-4 text-center text-sm text-muted-foreground">Ninguém apertou "Amém" hoje ainda.</p>
              )}
            </div>
          </section>

          {/* Confirmations */}
          <AlertDialog open={confirmReplace} onOpenChange={setConfirmReplace}>
            <AlertDialogContent data-testid="replace-confirm-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle className="font-display">Substituir o vídeo atual?</AlertDialogTitle>
                <AlertDialogDescription>
                  Já existe um vídeo publicado. Ao confirmar, ele será substituído. O QR Code, o link e suas informações de contato continuam os mesmos.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="replace-cancel-button">Cancelar</AlertDialogCancel>
                <AlertDialogAction data-testid="replace-confirm-button" onClick={() => { setConfirmReplace(false); doPublish(); }}>Sim, substituir</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          <AlertDialog open={confirmRemoveVideo} onOpenChange={setConfirmRemoveVideo}>
            <AlertDialogContent data-testid="remove-video-confirm-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle className="font-display">Remover apenas o vídeo?</AlertDialogTitle>
                <AlertDialogDescription>
                  O vídeo sairá da página pública (mostrará "Nenhum vídeo disponível"). Suas informações de contato (WhatsApp, Pix, redes) serão mantidas.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="remove-video-cancel-button">Cancelar</AlertDialogCancel>
                <AlertDialogAction data-testid="remove-video-confirm-button" onClick={() => { setConfirmRemoveVideo(false); handleRemoveVideo(); }}>Sim, remover vídeo</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          <AlertDialog open={confirmClearContact} onOpenChange={setConfirmClearContact}>
            <AlertDialogContent data-testid="remove-contact-confirm-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle className="font-display">Remover as informações de contato?</AlertDialogTitle>
                <AlertDialogDescription>
                  WhatsApp, Pix, Instagram e Facebook deixarão de aparecer na página. O vídeo continua no ar normalmente.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="remove-contact-cancel-button">Cancelar</AlertDialogCancel>
                <AlertDialogAction data-testid="remove-contact-confirm-button" onClick={() => { setConfirmClearContact(false); handleClearContact(); }}>Sim, remover informações</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      )}
    </motion.div>
  );
};
